/**
 * MoneyInput — free-text deal-value field.
 *
 * Accepts commas / currency symbols (so "250,000" is welcome), shows the
 * grouped value when not focused, and a clean unformatted number while
 * editing so the caret never jumps mid-type. Emits the parsed numeric
 * value via `onChange`. Replaces the old `type="number"` input that
 * silently rejected commas.
 */

import { useEffect, useState } from 'react';
import { formatMoneyValue, parseMoneyInput, sanitizeMoneyText } from '../format';

interface Props {
  value: number;
  onChange: (next: number) => void;
  id?: string;
  className?: string;
  placeholder?: string;
  ariaLabel?: string;
}

export function MoneyInput({ value, onChange, id, className, placeholder, ariaLabel }: Props) {
  const [focused, setFocused] = useState(false);
  const [text, setText] = useState(() => formatMoneyValue(value));

  // Re-sync the display when the external value changes and we're not editing.
  useEffect(() => {
    if (!focused) setText(formatMoneyValue(value));
  }, [value, focused]);

  return (
    <input
      id={id}
      type="text"
      inputMode="decimal"
      value={text}
      placeholder={placeholder}
      aria-label={ariaLabel}
      onFocus={() => {
        setFocused(true);
        setText(value ? String(value) : '');
      }}
      onChange={(e) => {
        const sanitized = sanitizeMoneyText(e.target.value);
        setText(sanitized);
        onChange(parseMoneyInput(sanitized));
      }}
      onBlur={() => {
        setFocused(false);
        setText(formatMoneyValue(value));
      }}
      className={className}
    />
  );
}
